import DatePicker, { registerLocale } from 'react-datepicker';
import { tr } from 'date-fns/locale';
import 'react-datepicker/dist/react-datepicker.css';
import { forwardRef, useState } from 'react';
import { Calendar, Clock, X } from 'lucide-react';
import clsx from 'clsx';

// Türkçe locale kaydet
registerLocale('tr', tr);

/**
 * CustomInput - Özel input görünümü
 */
const CustomInput = forwardRef(({ value, onClick, onClear, placeholder, hasValue, showTime, disabled, error }, ref) => (
    <div className="relative">
        <button
            type="button"
            ref={ref}
            onClick={onClick}
            disabled={disabled}
            className={clsx(
                "w-full flex items-center gap-2 px-3 py-2 bg-bg-secondary border rounded-lg text-left transition-colors",
                "focus:outline-none focus:ring-2 focus:ring-primary",
                error ? "border-error" : "border-border-light hover:border-border-default",
                disabled && "opacity-50 cursor-not-allowed"
            )}
        >
            {showTime ? (
                <Clock size={16} className="text-text-muted flex-shrink-0" />
            ) : (
                <Calendar size={16} className="text-text-muted flex-shrink-0" />
            )}
            <span className={clsx(
                "flex-1 truncate",
                hasValue ? "text-text-primary" : "text-text-muted"
            )}>
                {value || placeholder}
            </span>
            {hasValue && !disabled && (
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        onClear?.();
                    }}
                    className="p-0.5 hover:bg-bg-hover rounded transition-colors"
                >
                    <X size={14} className="text-text-muted" />
                </button>
            )}
        </button>
    </div>
));

CustomInput.displayName = 'CustomInput';

/**
 * DatePickerInput - react-datepicker için reusable wrapper component
 * 
 * @param {Object} props
 * @param {Date} props.value - Seçili tarih
 * @param {Function} props.onChange - Değişiklik callback
 * @param {string} props.placeholder - Placeholder text
 * @param {boolean} props.showTime - Zaman seçici göster
 * @param {boolean} props.showTimeOnly - Sadece zaman seçici
 * @param {string} props.dateFormat - Tarih formatı
 * @param {Date} props.minDate - Minimum tarih
 * @param {Date} props.maxDate - Maximum tarih
 * @param {boolean} props.disabled - Disabled durumu
 * @param {boolean} props.clearable - Temizlenebilir
 * @param {string} props.error - Hata mesajı
 * @param {string} props.label - Label
 * @param {boolean} props.required - Zorunlu alan
 * @param {string} props.className - Ek CSS sınıfları
 */
export default function DatePickerInput({
    value,
    onChange,
    placeholder = 'Tarih seçin',
    showTime = false,
    showTimeOnly = false,
    dateFormat,
    minDate,
    maxDate,
    disabled = false,
    clearable = true,
    error,
    label,
    required = false,
    className = '',
    ...rest
}) {
    const [isOpen, setIsOpen] = useState(false);

    // Otomatik format belirleme
    const getDateFormat = () => {
        if (dateFormat) return dateFormat;
        if (showTimeOnly) return 'HH:mm';
        if (showTime) return 'dd.MM.yyyy HH:mm';
        return 'dd.MM.yyyy';
    };

    // Placeholder belirleme
    const getPlaceholder = () => {
        if (placeholder !== 'Tarih seçin') return placeholder;
        if (showTimeOnly) return 'Saat seçin';
        if (showTime) return 'Tarih ve saat seçin';
        return 'Tarih seçin';
    };

    const handleClear = () => {
        onChange?.(null);
    };

    return (
        <div className={clsx("date-picker-wrapper", className)}>
            {label && (
                <label className="block text-sm font-medium text-text-secondary mb-1">
                    {label}
                    {required && <span className="text-error ml-1">*</span>}
                </label>
            )}
            <DatePicker
                selected={value}
                onChange={onChange}
                locale="tr"
                dateFormat={getDateFormat()}
                placeholderText={getPlaceholder()}
                showTimeSelect={showTime || showTimeOnly}
                showTimeSelectOnly={showTimeOnly}
                timeFormat="HH:mm"
                timeIntervals={15}
                timeCaption="Saat"
                minDate={minDate}
                maxDate={maxDate}
                disabled={disabled}
                onCalendarOpen={() => setIsOpen(true)}
                onCalendarClose={() => setIsOpen(false)}
                customInput={
                    <CustomInput
                        placeholder={getPlaceholder()}
                        hasValue={!!value}
                        showTime={showTime || showTimeOnly}
                        onClear={clearable ? handleClear : undefined}
                        disabled={disabled}
                        error={error}
                    />
                }
                popperClassName="date-picker-popper"
                calendarClassName="date-picker-calendar"
                {...rest}
            />
            {error && (
                <p className="text-xs text-error mt-1">{error}</p>
            )}
        </div>
    );
}

// Range picker için ayrı export
export function DateRangePicker({
    startDate,
    endDate,
    onStartChange,
    onEndChange,
    startLabel = 'Başlangıç',
    endLabel = 'Bitiş',
    className = '',
    ...rest
}) {
    return (
        <div className={clsx("flex gap-4", className)}>
            <DatePickerInput
                value={startDate}
                onChange={onStartChange}
                label={startLabel}
                maxDate={endDate}
                {...rest}
            />
            <DatePickerInput
                value={endDate}
                onChange={onEndChange}
                label={endLabel}
                minDate={startDate}
                {...rest}
            />
        </div>
    );
}
